// src/components/Postview.jsx
import React, { useCallback, useEffect, useMemo, useState, useContext, useRef } from "react";
import { useHistory } from "react-router-dom";
import CircularProgress from "@material-ui/core/CircularProgress";
import { useTranslation } from "react-i18next";
import { SnackbarContext } from "../context/SnackbarContext";
import { useAPI } from "../context/ApiContext";
import { useOnScreen } from "../utils/useOnScreen";
import { Menu, MenuItem, IconButton } from "@material-ui/core";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import ShareIcon from "@material-ui/icons/Share";
import DeleteIcon from "@material-ui/icons/Delete";
import ReportIcon from "@material-ui/icons/Report";
import copy from "copy-to-clipboard";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import { gaEvent, getInitialUtm } from "../lib/ga";
import { FaLine, FaTwitter, FaWhatsapp, FaFacebookMessenger } from "react-icons/fa";
import { t } from "i18next";


/* ===== helper: 本文のURLを <a> にする（http/https + www. 対応） ===== */
const URL_REGEX = /(https?:\/\/[^\s]+|www\.[^\s]+)/gi;
const normalizeHref = (url) => (url.startsWith("http") ? url : `https://${url}`);
const LinkifiedText = ({ text, stopBubble = false }) => {
  if (!text) return null;
  const parts = String(text).split(URL_REGEX);
  return parts.map((part, i) => {
    if (!part) return null;
    const isUrl = URL_REGEX.test(part);
    URL_REGEX.lastIndex = 0; // reset
    if (!isUrl) return <span key={i}>{part}</span>;
    const href = normalizeHref(part);
    const onClick = stopBubble ? (e) => e.stopPropagation() : undefined;
    return (
      <a key={i} href={href} target="_blank" rel="noopener noreferrer" onClick={onClick}>
        {part}
      </a>
    );
  });
};

// 端末判定（必要十分の軽い判定）
const isMobile = () =>
  typeof navigator !== "undefined" && /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

const isMobileSafari = () =>
  typeof navigator !== "undefined" &&
  /iP(hone|ad|od)/.test(navigator.userAgent) &&
  /Safari/.test(navigator.userAgent) &&
  !/CriOS|FxiOS|EdgiOS/.test(navigator.userAgent);


/* ===== Link preview helpers ===== */
const YouTubeEmbed = ({ embedUrl, title }) => {
  if (!embedUrl) return null;
  return (
    <div style={{ position: "relative", width: "100%", paddingTop: "56.25%", borderRadius: 12, overflow: "hidden" }}>
      <iframe
        src={embedUrl}
        title={title || "YouTube video"}
        style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", border: 0 }}
        loading="lazy"
        referrerPolicy="strict-origin-when-cross-origin"
        allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowFullScreen
      />
    </div>
  );
};
const TweetEmbed = ({ embedUrl }) => {
  if (!embedUrl) return null;
  return (
    <div style={{ position: "relative", width: "100%", minHeight: 220, borderRadius: 12, overflow: "hidden" }}>
      <iframe src={embedUrl} title="Tweet" style={{ width: "100%", height: 420, border: 0 }} loading="lazy" />
    </div>
  );
};
// 置き換え
const InstagramEmbed = ({ embedUrl, openUrl, poster }) => {
  if (!embedUrl) return null;

  // モバイルでは iframe をやめて安全に外部遷移（再生不可問題の回避）
  if (isMobile() || isMobileSafari()) {
    const href = openUrl || embedUrl.replace(/\/embed.*$/, ""); // 元投稿URLへ寄せる
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
        className="text-decoration-none"
        style={{ display: "block", position: "relative", borderRadius: 12, overflow: "hidden" }}
      >
        <img
          src={poster || "/assets/images/ig_poster_fallback.jpg"}
          alt="Instagram"
          style={{ width: "100%", display: "block", background: "#000", maxHeight: 520, objectFit: "cover" }}
          onError={(e) => (e.currentTarget.style.opacity = 0.15)}
        />
        {/* 再生オーバーレイ */}
        <span
          style={{
            position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center",
            background: "rgba(0,0,0,.25)"
          }}
        >
          <span
            className="btn btn-sm bg-current text-white fw-700 font-xssss"
            style={{ borderRadius: 9999 }}
          >
            <i className="feather-external-link me-1" /> Instagramで再生
          </span>
        </span>
      </a>
    );
  }
    // PC: 16:9 のレスポンシブ iframe
    return (
      <div style={{ position: "relative", width: "100%", paddingTop: "56.25%", borderRadius: 12, overflow: "hidden", background: "#000" }}>
        <iframe
          src={embedUrl}
          title="TikTok"
          allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; fullscreen"
          allowFullScreen
          loading="lazy"
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: 0 }}
        />
      </div>
    );
  };
  
  const TikTokEmbed = ({ embedUrl, openUrl, poster }) => {
    if (!embedUrl) return null;
  
    // モバイルは外部遷移（iframe 再生やスクロールの相性を避ける）
    if (isMobile() || isMobileSafari()) {
      const href = openUrl || embedUrl;
      return (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="text-decoration-none"
          style={{ display: "block", position: "relative", borderRadius: 12, overflow: "hidden" }}
        >
          <img
            src={poster || "/assets/images/tiktok_poster_fallback.jpg"}
            alt="TikTok"
            style={{ width: "100%", display: "block", background: "#000", maxHeight: 520, objectFit: "cover" }}
            onError={(e) => (e.currentTarget.style.opacity = 0.15)}
          />
          <span
            style={{
              position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center",
              background: "rgba(0,0,0,.25)"
            }}
          >
            <span className="btn btn-sm bg-current text-white fw-700 font-xssss" style={{ borderRadius: 9999 }}>
              <i className="feather-external-link me-1" /> TikTokで再生
            </span>
          </span>
        </a>
      );
    }
    
  // PC向け: 可能なら iframe を使う（自動再生制限の影響が少ない）
  let src = embedUrl;
  try {
    const u = new URL(embedUrl);
    u.searchParams.set("omit_script", "true");
    u.searchParams.set("hidecaption", "true");
    src = u.toString();
  } catch {}

  return (
    <div style={{ position: "relative", width: "100%", paddingTop: "56.25%", borderRadius: 12, overflow: "hidden", background: "#000" }}>
      <iframe
        key={src}                           // URL変化で確実に再マウント
        src={src}
        title="Instagram"
        // モバイルの制限を少しでも緩和（PCで有効）
        allow="autoplay *; encrypted-media *; clipboard-write; picture-in-picture; fullscreen"
        allowFullScreen
        // iOSでの referrer 問題を避けるなら外してもOK（必要なら調整）
        referrerPolicy="strict-origin-when-cross-origin"
        // ※ モバイルでは lazy を使わない
        loading={isMobile() ? undefined : "lazy"}
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: 0 }}
      />
    </div>
  );
};



const LinkPreviewCard = ({ preview, url, loading, error }) => {
  const api = useAPI();
  const imageProxyUrl = api?.imageProxyUrl || ((u) => u);
  const host = useMemo(() => { try { return new URL(url).hostname; } catch { return ""; } }, [url]);
  const favicon = host ? `https://www.google.com/s2/favicons?domain=${encodeURIComponent(host)}&sz=64` : "";
// 上部のヘルパー付近に追加
const isMobileUA = () =>
  typeof navigator !== "undefined" &&
  /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  

  if (!url) return null;
  if (loading) {
    return (
      <div className="card w-100 shadow-xss rounded-xxl border-light-md p-3 mb-2">
        <div className="d-flex align-items-center">
          <CircularProgress size={18} className="me-2" />
          <span className="font-xssss text-grey-600">{t("PostView101")}</span>
        </div>
      </div>
    );
  }
  if (error) {
    return (
        <a href={url} target="_blank" rel="noopener noreferrer" onClick={(e)=>e.stopPropagation()} className="text-decoration-none">
        <div className="card w-100 shadow-xss rounded-xxl border-light-md p-3 mb-2">
        <div className="d-flex align-items-center">
          <img src={favicon} alt="" className="me-2" style={{ width: 20, height: 20, borderRadius: 4 }} />
          <div className="flex-fill">
            <div className="font-xssss text-danger">{t("PostView102")}</div>
            <div className="font-xssss text-grey-600 text-truncate">{url}</div>
          </div>
        </div>
      </div>
      </a>
    );
  }

  const resolveImage = (u) => {
    if (!u) return "";
    try {
      const a = new URL(u);
      return a.protocol === "http:" ? imageProxyUrl(u) : u;
    } catch {
      return u;
    }
  };

  if (preview?.kind === "image") {
    const src = resolveImage(preview.image || url);
    return (
<a href={url} target="_blank" rel="noopener noreferrer" onClick={(e)=>e.stopPropagation()} className="text-decoration-none">
      <div className="card w-100 shadow-xss rounded-xxl border-light-md p-3 mb-2">

        {src && (
          <img
            src={src}
            alt=""
            className="rounded-xxl"
            style={{ width: "100%", maxHeight: 420, objectFit: "contain", background: "#f6f6f6" }}
            onError={(e) => (e.currentTarget.style.display = "none")}
          />
        )}
        <div className="font-xssss text-grey-500 mt-2 text-truncate">{host}</div>
      </div>
      </a>
    );
  }

  if (preview?.kind === "youtube") {
    return (
      <div className="card w-100 shadow-xss rounded-xxl border-light-md p-3 mb-2">
        <YouTubeEmbed embedUrl={preview.embedUrl} title={preview.title || "YouTube"} />
        <div className="mt-2">
          <div className="font-xsss fw-700 text-dark text-truncate">{preview.title || host}</div>
          <div className="font-xssss text-grey-500 mt-1 text-truncate">{host}</div>
                    <div className="mt-1">
            <a href={url} target="_blank" rel="noopener noreferrer" onClick={(e)=>e.stopPropagation()} className="font-xssss">
              <i className="feather-external-link me-1"></i>{t("PostView103")}
            </a>
          </div>
        </div>
      </div>
    );
  }

  if (preview?.kind === "tweet") {
    return (
      <div className="card w-100 shadow-xss rounded-xxl border-light-md p-3 mb-2">
        <TweetEmbed embedUrl={preview.embedUrl} />
        <div className="mt-2">
          <div className="font-xssss text-grey-500 text-truncate">{host}</div>
                    <div className="mt-1">
            <a href={url} target="_blank" rel="noopener noreferrer" onClick={(e)=>e.stopPropagation()} className="font-xssss">
              <i className="feather-external-link me-1"></i>{t("PostView103")}
            </a>
          </div>
        </div>
      </div>
    );
  }

  if (preview?.kind === "instagram") {
    const thumb = resolveImage(preview.image);
    const openUrl = url; // 元URL（/reel/… /p/… など）
  
    return (
      <div className="card w-100 shadow-xss rounded-xxl border-light-md p-3 mb-2">
        {preview.embedUrl ? (
          <InstagramEmbed embedUrl={preview.embedUrl} openUrl={openUrl} poster={thumb} />
        ) : (
          // 画像しか無い場合は静的カード
          thumb && (
            <a href={openUrl} target="_blank" rel="noopener noreferrer" onClick={(e)=>e.stopPropagation()}>
              <img
                src={thumb}
                alt=""
                className="rounded-xxl"
                style={{ width: "100%", maxHeight: 480, objectFit: "cover", background: "#000" }}
                onError={(e) => (e.currentTarget.style.display = "none")}
              />
            </a>
          )
        )}
      </div>
    );
  }

  if (preview?.kind === "tiktok") {
    const thumb = resolveImage(preview.image);
    const openUrl = url;
    return (
      <div className="card w-100 shadow-xss rounded-xxl border-light-md p-3 mb-2">
        <TikTokEmbed embedUrl={preview.embedUrl} openUrl={openUrl} poster={thumb} />
        <div className="mt-2">
          <div className="font-xssss text-grey-500 text-truncate">{host}</div>
          <div className="mt-1">
            <a href={openUrl} target="_blank" rel="noopener noreferrer" onClick={(e)=>e.stopPropagation()} className="font-xssss">
              <i className="feather-external-link me-1"></i>{t("PostView103")}
            </a>
          </div>
        </div>
      </div>
    );
  }

  


  const og = {
    title: preview?.title || host,
    description: preview?.description || "",
    image: resolveImage(preview?.image || ""),
  };

  return (
      <a href={url} target="_blank" rel="noopener noreferrer" onClick={(e)=>e.stopPropagation()} className="text-decoration-none">
      <div className="card w-100 shadow-xss rounded-xxl border-light-md p-3 mb-2">
      <div className="d-flex">
        {og.image ? (
          <img
            src={og.image}
            alt=""
            className="me-3 rounded-xxl"
            style={{ width: 96, height: 96, objectFit: "cover" }}
            onError={(e) => (e.currentTarget.style.display = "none")}
          />
        ) : (
          <img src={favicon} alt="" className="me-3" style={{ width: 32, height: 32, borderRadius: 6 }} />
        )}
        <div className="flex-fill" style={{ minWidth: 0 }}>
          <div className="font-xsss fw-700 text-dark text-truncate">{og.title}</div>
          {og.description && (
            <div
              className="font-xssss text-grey-600"
              style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}
            >
              {og.description}
            </div>
          )}
          <div className="font-xssss text-grey-500 mt-1 text-truncate">{host}</div>
        </div>
      </div>
    </div>
    </a>
  );
};

/* ===== Postview (nicelinks 版) ===== */
const Postview = (props) => {
  const { t } = useTranslation();
  const api = useAPI();
  const { newSnack } = useContext(SnackbarContext);
  const history = useHistory();

  // props互換
  const post = useMemo(() => props.post || props.postData || {}, [props.post, props.postData]);
  const author = useMemo(() => post.profile || props.profileData || {}, [post.profile, props.profileData]);
  const repostingProfile = useMemo(
      () => props.repostingProfileData || post.repostingProfile || null,
      [props.repostingProfileData, post.repostingProfile]
    );
  const isRepostItem = !!(post.isRepostItem || repostingProfile);

  const postId = post.postId || post.id;
  const createdAt = post.created_at || post.createdOn || post.created || "";
  const feedCreatedAt = post.feedCreatedAt || createdAt; // リポストならリポスト時刻を優先
  const commentedCountInit = post.commentedCount ?? props.commentedCounts ?? 0;
  const repostedCountInit = post.repostedCount ?? props.repostedTimes ?? 0;
  const niceCountInit = post.niceCount ?? props.likedTimes ?? 0;
  const bookmarkCountInit = post.bookmarkCount ?? 0;
  const isNicedInit = !!post.isNicedByMe;
  const isRepostedInit = !!post.isRepostedByMe; // ★ serializer で追加（後述）
  const [repostBusy, setRepostBusy] = useState(false);
  const isMine = !!post.isMine;                 // ★ serializer で追加（後述）
  const isBookmarkedInit = !!post.isBookmarkedByMe;

  const [isLiked, setIsLiked] = useState(isNicedInit);
  const [isReposted, setIsReposted] = useState(isRepostedInit);
  const [likeCount, setLikeCount] = useState(niceCountInit);
   const [isBookmarked, setIsBookmarked] = useState(isBookmarkedInit);
 const [bookmarkCount, setBookmarkCount] = useState(bookmarkCountInit);
  const [repostCount, setRepostCount] = useState(repostedCountInit);
  const [commentCount, setCommentCount] = useState(commentedCountInit);
  const [comments, setComments] = useState([]);
  const [showComments, setShowComments] = useState(false);
  const [commentText, setCommentText] = useState("");
  const isSensitive = !!post.sensitiveFlag;
  const canViewSensitive = api.isAuthenticated && !!api.clientSettings?.view_sensitive;
  const showSensitiveGate = isSensitive && !canViewSensitive;
  const [anchorEl, setAnchorEl] = useState(null);
  const handleMenuOpen = (e) => setAnchorEl(e.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const gateMessage = api.isAuthenticated
    ? (t("Postview7")|| "成人向け、またはセンシティブな投稿です。閲覧するには設定から閲覧オンにしてください")
    : (t("Postview8") || "成人向け、またはセンシティブな投稿です。閲覧するにはログインしてください");


  

  const goDetail = useCallback(() => {
      if (!postId) return;
      history.push(`/post/${postId}`);
    }, [history, postId]);

    // ★ 追加: ユーザー詳細へ
    const goUser = useCallback(
      (userId, e) => {
        e?.stopPropagation?.();
        if (!userId) return;
        history.push(`/user/${userId}`);
      },
      [history]
    );
  

  // URLプレビュー
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState("");
  const [preview, setPreview] = useState(null);
  const postUrl = post.postUrl && post.urlPostFlag ? post.postUrl : null;

   // ---- unfurl は「見えたら1回だけ」＋ queue 経由でスロットル＆キャッシュ ----
   const [cardRef, onScreen] = useOnScreen("400px");
   const hasRequestedRef = useRef(false);
   useEffect(() => {
     if (!postUrl) return;
     if (showSensitiveGate) return;       // センシはスキップ
     if (!onScreen) return;               // 画面に入ってから
     if (hasRequestedRef.current) return; // 1度だけ
     hasRequestedRef.current = true;
     let alive = true;
     setPreviewLoading(true);
     setPreviewError("");
     (async () => {
       try {
         const data = await api.unfurlUrl(postUrl); // ★ queue キャッシュ
         if (!alive) return;
         if (data?.ok) setPreview(data);
         else setPreviewError(data?.error || "failed");
       } catch (e) {
         if (!alive) return;
         // 429 でも UI は壊さずリンクのみ表示
         setPreviewError(e?.message || "failed");
       } finally {
         if (alive) setPreviewLoading(false);
       }
     })();
     return () => { alive = false; };
   }, [api, postUrl, onScreen, showSensitiveGate]);

  const toggleComments = async () => {
        // タイムラインでは「入力欄だけ」開く。コメント一覧は取得しない。
        setShowComments((v) => !v);
       };

  // Nice トグル
  const handleToggleNice = async () => {
    if (!api.isAuthenticated) return newSnack("error", t("Header14") || "ログインが必要です");
    const wantNice = !isLiked;
    try {
      await api.toggleNice(postId, wantNice);
      setIsLiked(wantNice);
      setLikeCount((c) => c + (wantNice ? 1 : -1));
    } catch (e) {
      newSnack("error", e?.data?.detail || t("Postview1"));
    }
  };

  // Repost トグル
  const handleToggleRepost = async () => {
    if (!api.isAuthenticated) return newSnack("error", t("Header14") || "ログインが必要です");
    if (repostBusy) return;
    const wantRepost = !isReposted;
    setRepostBusy(true);
    try {
      await api.toggleRepost(postId, wantRepost);
      setIsReposted(wantRepost);
      setRepostCount((c) => c + (wantRepost ? 1 : -1));
      newSnack("success", wantRepost ? (t("Postview2") || "リポストしました") : (t("Postview3") || "リポストを取り消しました"));
    } catch (e) {
      newSnack("error", e?.data?.detail || t("Postview1"));
    }
    finally {
        setRepostBusy(false);
      }
      if (!wantRepost && isRepostItem && repostingProfile?.userId === api.myUserId) {
        props.onDeleted?.(postId);
      }
  };

   // Bookmark トグル
 const handleToggleBookmark = async () => {
   if (!api.isAuthenticated) return newSnack("error", t("Header14") || "ログインが必要です");
   const want = !isBookmarked;
   try {
     await api.toggleBookmark(postId, want);
     setIsBookmarked(want);
     setBookmarkCount((c) => c + (want ? 1 : -1));
   } catch (e) {
     newSnack("error", e?.data?.detail || t("Postview1"));
   }
 };

  // コメント投稿
  const submitComment = async (e) => {
    e?.preventDefault?.();
    if (!api.isAuthenticated) return newSnack("error", t("Header14") || "ログインが必要です");
    const text = commentText.trim();
    if (!text) return;
    try {
      await api.addComment(postId, text);
      setCommentText("");
      setCommentCount((c) => c + 1);
    } catch (e2) {
      newSnack("error", e2?.data?.detail || t("Postview1"));
    }
  };

  // 投稿削除
  const onDeletePost = async () => {
    if (!api.isAuthenticated) return;
    if (!window.confirm(t("Postview4"))) return;
    try {
      await api.deletePost(postId);
      newSnack("success", t("Postview5"));
      props.onDeleted?.(postId);
    } catch (e) {
      newSnack("error", e?.data?.detail || t("Postview6"));
    }
  };

  const createdDisplay = useMemo(() => {
    if (!feedCreatedAt) return "";
      try {
        const d = new Date(feedCreatedAt);
      return Number.isNaN(d.getTime()) ? String(feedCreatedAt) : d.toLocaleString();
      } catch {
      return String(feedCreatedAt);
      }
  }, [feedCreatedAt]);

  if (!postId || !author) return null;

// ===== Share helpers =====
const PUBLIC_ORIGIN = (process.env.REACT_APP_PUBLIC_URL || window.location.origin).replace(/\/+$/,'');
// og は必ず“API系”のオリジンを優先（OG展開の確実性を担保）
const OG_ORIGIN = (process.env.REACT_APP_API_BASE || process.env.REACT_APP_API_URL || PUBLIC_ORIGIN).replace(/\/+$/,'');

// UTMを付与（流入元計測）
const withUtm = (url, {src="share_menu", med="copy", camp="post_share"}={}) => {
  const u = new URL(url);
  u.searchParams.set("utm_source", src);
  u.searchParams.set("utm_medium", med);
  u.searchParams.set("utm_campaign", camp);
  return u.toString();
};

const shareText = (t, raw) => {
  const oneLiner = (raw || "").trim().split("\n")[0].slice(0, 60);
  // 言語別に一言（i18nキーはお好みで）
  const ask = (t("PostView120") || "意見ください👇");
  return oneLiner ? `${ask}\n${oneLiner}` : ask;
};

// 各サービスの共有URL（ogURL を使う）
const buildShareTargets = ({ ogURL, title, text }) => {
  const urlEnc = encodeURIComponent(ogURL);
  const textEnc = encodeURIComponent(text || "");
  const titleEnc = encodeURIComponent(title || "");
  return {
    line: `https://line.me/R/msg/text/?${encodeURIComponent((text||title)||"")}%20${urlEnc}`,
    x: `https://twitter.com/intent/tweet?text=${textEnc}&url=${urlEnc}`,
    whatsapp: `https://api.whatsapp.com/send?text=${textEnc}%20${urlEnc}`,
    messenger: `https://www.facebook.com/dialog/send?app_id=87741124305&link=${urlEnc}&redirect_uri=${urlEnc}`, // app_idは汎用。将来差し替え可
  };
};

// Web Share API（使えなければ false）
const tryWebShare = async ({ title, text, url }) => {
  if (navigator.share) {
    try {
      await navigator.share({ title, text, url });
      return true;
    } catch {
      /* キャンセル等は無視してフォールバック */
    }
  }
  return false;
};


  return (
    <>
    <style>{`
  .nl-share-mini { gap: 5px !important; }             /* 余白も小さく */
  .nl-share-mini a {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 22px;                                       /* タップ領域も最小限 */
    height: 22px;
    border-radius: 9999px;
  }
  .nl-share-mini i {
    font-size: 13px;                                   /* アイコンを約1/3へ */
    line-height: 1;
  }
`}</style>
    <div
      className="card w-100 shadow-xss rounded-xxl border-light-md ps-3 pe-3 pt-3 pb-2 mb-2"
      onClick={goDetail}
      style={{ cursor: "pointer", position: "relative" }} // overlay用に relative
      ref={cardRef}
    >

      {/* Repost バッジ（上部） */}
      {isRepostItem && repostingProfile && (
        <div className="card-body ps-1 pe-1 pt-0 pb-2 d-flex align-items-center text-grey-600 font-xssss">
          <i className="feather-repeat btn-round-xs me-2"></i>
          <img
            src={repostingProfile.image || "/assets/images/default_profile.png"}
            alt=""
            className="rounded-circle me-2 pointer"
            style={{ width: 18, height: 18, cursor: "pointer" }}
            onClick={(e) => goUser(repostingProfile.userId, e)}
          />
          <span className="fw-600">{repostingProfile.nickname || "User"}</span>
          <span className="ms-1">さんがリポスト</span>
        </div>
      )}
      {/* ヘッダ */}
      <div className="card-body ps-1 pe-1 pt-0 pb-0 d-flex position-relative">
        <figure
          className="avatar me-3 pointer"
          onClick={(e) => goUser(author.userId, e)}
          style={{ cursor: "pointer" }}
        >
          <img
            src={author.image || "/assets/images/default_profile.png"}
            alt="avatar"
            className="shadow-sm rounded-circle w40"
          />
        </figure>
        <h4 className="fw-700 text-grey-900 font-xssss mt-1">
          {author.nickname || "User"}
          <span className="d-block font-xssss fw-500 mt-1 lh-3 text-grey-500">{createdDisplay}</span>
        </h4>

      </div>
      <div className="position-absolute right-0 top-0 me-1">
  <IconButton onClick={(e) => { e.stopPropagation(); handleMenuOpen(e); }}>
    <MoreVertIcon fontSize="small" />
  </IconButton>
</div>
<Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
  {/* 共有（ネイティブ→各サービス） */}
  <MenuItem
    onClick={async () => {
      const ogURL = withUtm(`${OG_ORIGIN}/og/post/${postId}`, { src:"share_menu", med:"system_share", camp:"post_share" });
      const humanURL = `${PUBLIC_ORIGIN}/post/${postId}`; // 参考（UIで表示/コピーしたい場合に使う）
      const title = (post.postText || "").slice(0, 60) || "Nicelinks";
      const text = shareText(t, post.postText);
      const ok = await tryWebShare({ title, text, url: ogURL });
      if (ok) {
        // window.ReactGA?.event({category:"share", action:"web_share", label:"post"}) // 使っていれば
        newSnack("success", t("PostView105") || "共有しました");
      } else {
        // フォールバック（コピー）
        copy(ogURL);
        // window.ReactGA?.event({category:"share", action:"copy_fallback", label:"post"})
        newSnack("success", t("PostView106") || "URLをコピーしました");
      }
      handleMenuClose();
    }}
  >
    <ShareIcon fontSize="small" className="me-2" /> {t("PostView107") || "シェアする"}
  </MenuItem>

  {/* SNSショートカット */}
  {(() => {
    const ogURL = withUtm(`${OG_ORIGIN}/og/post/${postId}`, { src:"share_menu", med:"sns", camp:"post_share" });
    const title = (post.postText || "").slice(0, 60) || "Nicelinks";
    const text = shareText(t, post.postText);
    const links = buildShareTargets({ ogURL, title, text });
    return (
      <>
        <MenuItem
          component="a"
          href={links.line}
          target="_blank"
          rel="noopener noreferrer"
          onClick={handleMenuClose}
        >
          <FaLine className="me-2" color="#00c300" size={16} /> {t("PostView108")}
        </MenuItem>

        <MenuItem
          component="a"
          href={links.x}
          target="_blank"
          rel="noopener noreferrer"
          onClick={handleMenuClose}
        >
          <FaTwitter className="me-2" color="#1DA1F2" size={16} /> {t("PostView109")}
        </MenuItem>

        <MenuItem
          component="a"
          href={links.whatsapp}
          target="_blank"
          rel="noopener noreferrer"
          onClick={handleMenuClose}
        >
          <FaWhatsapp className="me-2" color="#25D366" size={16} /> {t("PostView110")}
        </MenuItem>

        <MenuItem
          component="a"
          href={links.messenger}
          target="_blank"
          rel="noopener noreferrer"
          onClick={handleMenuClose}
        >
          <FaFacebookMessenger className="me-2" color="#0084FF" size={16} /> {t("PostView111")}
        </MenuItem>

      </>
    );
  })()}

  {/* コピー（明示） */}
  <MenuItem
    onClick={() => {
      const ogURL = withUtm(`${OG_ORIGIN}/og/post/${postId}`, { src:"share_menu", med:"copy", camp:"post_share" });
      copy(ogURL);
      // window.ReactGA?.event({category:"share", action:"copy", label:"post"})
      newSnack("success", t("PostView106") || "URLをコピーしました");
      gaEvent("og_copy", { post_id: postId, ...getInitialUtm(), from: "menu" });
      handleMenuClose();
    }}
  >
    <i className="feather-copy me-2" /> {t("PostView112") || "リンクをコピー"}
  </MenuItem>

  {/* 参考：投稿を開く（自分用） */}
  <MenuItem
    component="a"
    href={`${PUBLIC_ORIGIN}/post/${postId}`}
    target="_blank"
    rel="noopener noreferrer"
    onClick={handleMenuClose}
  >
    <OpenInNewIcon fontSize="small" className="me-2" /> {t("PostView113") || "投稿ページを開く"}
  </MenuItem>

  {/* 既存：削除・報告（そのまま） */}
  {isMine && (
    <MenuItem
      onClick={async (e) => {
        e.stopPropagation();
        if (!api.isAuthenticated) {
          newSnack("error", t("Header14") || "ログインしてください");
          handleMenuClose(); return;
        }
        await onDeletePost();
        handleMenuClose();
      }}
    >
      <DeleteIcon fontSize="small" className="me-2" /> {t("PostView114") || "投稿を削除"}
    </MenuItem>
  )}
  <MenuItem
    onClick={async () => {
      if (!api.isAuthenticated) {
        newSnack("error", t("Header14") || "ログインしてください");
        handleMenuClose(); return;
      }
      await api.reportPost(postId, "不適切");
      newSnack("success", t("PostView115") || "投稿を報告しました");
      handleMenuClose();
    }}
  >
    <ReportIcon fontSize="small" className="me-2" /> {t("PostView116") || "投稿を報告"}
  </MenuItem>
</Menu>

      {/* ↓↓↓ ここから「ブラー対象の本文＋プレビュー」をラップ ↓↓↓ */}
      <div className="position-relative">
        <div
          style={{
            filter: showSensitiveGate ? "blur(16px)" : "none",
            pointerEvents: showSensitiveGate ? "none" : "auto",
            transition: "filter 0.2s ease",
          }}
        >
          {/* 本文 */}
          {post.postText && (
            <div className="card-body p-0 me-lg-5">
              <p className="fw-600 text-grey-700 lh-26 font-xssss w-100 mb-2" style={{ whiteSpace: "pre-line" }}>
                <LinkifiedText text={post.postText} stopBubble />
              </p>
            </div>
          )}

          {/* URL プレビュー */}
          {postUrl && (
            <LinkPreviewCard
              preview={preview}
              url={postUrl}
              loading={previewLoading}
              error={previewError}
            />
          )}
        </div>

        {/* センシティブガードのオーバーレイ */}
        {showSensitiveGate && (
          <div
            className="d-flex align-items-center justify-content-center"
            style={{
              position: "absolute",
              inset: 0,
              zIndex: 5,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="bg-white shadow-lg rounded-xxl p-3 text-center"
              style={{ maxWidth: 520, width: "90%", opacity: 0.95 }}
            >
              <div className="font-xsss fw-700 mb-2 text-danger">
                {t("Postview9") || "センシティブ"}
              </div>
              <div className="font-xssss text-grey-700 mb-2">{gateMessage}</div>

              {!api.isAuthenticated ? (
                <a
                  href="/login"
                  className="btn btn-sm bg-current text-white fw-700 font-xssss"
                  onClick={(e) => e.stopPropagation()}
                >
                  {t("Postview10") || "ログイン"}
                </a>
              ) : (
                <a
                  href="/settings"
                  className="btn btn-sm bg-current text-white fw-700 font-xssss"
                  onClick={(e) => e.stopPropagation()}
                >
                  {t("Postview11") || "設定を開く"}
                </a>
              )}
            </div>
          </div>
        )}
      </div>

      {/* アクション行 */}
{/* アクション行（均等配置・数値のみ） */}
<div className="card-body p-0 mt-1" onClick={(e) => e.stopPropagation()}>
  <div className="d-flex w-100" style={{ gap: 0 }}>
    {/* Like */}
    <div
      role="button"
      aria-label="Nice"
      onClick={handleToggleNice}
      style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}
      className={`pointer fw-600 lh-26 font-xsssss ${
        isLiked ? "text-current" : "text-grey-600 text-dark"
      }`}
    >
      <i className={`feather-thumbs-up ${isLiked ? "text-current" : "text-dark text-grey-600"} btn-round-sm font-sm`}></i>
      <span className="ms-1 fw-700">{likeCount}</span>
    </div>

      {/* Repost（常に表示／isReposted で有効色、busy で半透明＆クリック無効） */}
  <div
    role="button"
    aria-label={isReposted ? "リポストを取り消す" : "リポストする"}
    title={isReposted ? "リポストを取り消す" : "リポスト"}
    onClick={repostBusy ? undefined : handleToggleRepost}
    style={{
      flex: 1,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      opacity: repostBusy ? 0.5 : 1,
      pointerEvents: repostBusy ? "none" : "auto",
    }}
    className={`pointer fw-600 lh-26 font-xsssss ${
      isReposted ? "text-current" : "text-grey-600 text-dark"
    }`}
  >
    <i className={`feather-repeat ${isReposted ? "text-current" : "text-dark text-grey-600"} btn-round-sm font-sm`}></i>
    <span className="ms-1 fw-700">{repostCount}</span>
  </div>



    {/* Comment */}
    <div
      role="button"
      aria-label="Comments"
      onClick={toggleComments}
      style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}
      className={`pointer fw-600 lh-26 font-xsssss ${showComments ? "text-current" : "text-grey-600 text-dark"}`}
    >
      <i className={`feather-message-circle ${showComments ? "text-current" : "text-dark text-grey-600"} btn-round-sm font-sm`}></i>
      <span className="ms-1 fw-700">{commentCount}</span>
    </div>
        {/* Bookmark */}
        <div
      role="button"
      aria-label="Bookmark"
      onClick={handleToggleBookmark}
      style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}
      className={`pointer fw-600 lh-26 font-xsssss ${
        isBookmarked ? "text-current" : "text-grey-600 text-dark"
      }`}
    >
      <i className={`feather-bookmark ${isBookmarked ? "text-current" : "text-dark text-grey-600"} btn-round-sm font-sm`}></i>
    </div>

    
  </div>
  <div className="card-body p-0 mt-0" onClick={(e)=>e.stopPropagation()}>
  {(() => {
    const ogURL = withUtm(`${OG_ORIGIN}/og/post/${postId}`, { src:"quickbar", med:"sns", camp:"post_share" });
    const title = (post.postText || "").slice(0, 60) || "Nicelinks";
    const text = shareText(t, post.postText);
    const links = buildShareTargets({ ogURL, title, text });
    return (
    <div className="d-flex justify-content-end nl-share-mini">
      <a href={links.line} target="_blank" rel="noopener noreferrer" title="LINE" aria-label="LINE">
        <FaLine color="#00c300" size={14} />
      </a>
      <a href={links.x} target="_blank" rel="noopener noreferrer" title="X" aria-label="X">
        <FaTwitter color="#1DA1F2" size={14} />
      </a>
      <a href={links.whatsapp} target="_blank" rel="noopener noreferrer" title="WhatsApp" aria-label="WhatsApp">
        <FaWhatsapp color="#25D366" size={14} />
      </a>
      <a href={links.messenger} target="_blank" rel="noopener noreferrer" title="Messenger" aria-label="Messenger">
        <FaFacebookMessenger color="#0084FF" size={14} />
      </a>
    </div>
    );
  })()}
</div>

</div>


      {/* コメント */}
      {showComments && (
        <div className="card-body d-block p-0 mt-2">

<form onSubmit={submitComment} className="d-flex align-items-center mt-2" onClick={(e) => e.stopPropagation()}>
  <input
    type="text"
    className="form-control font-xssss"
    placeholder={t("PostView117")}
    value={commentText}
    onChange={(e) => setCommentText(e.target.value)}
    // Ctrl/Cmd + Enter で送信（お好みで）
    onKeyDown={(e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "Enter" && commentText.trim()) {
        submitComment(e);
      }
    }}
  />

  {/* ★ アイコンボタン化 */}
  <button
    type="submit"
    aria-label={t("PostView118") || "送信"}
    title={t("PostView118") || "送信"}
    disabled={!commentText.trim()}
    className="ms-2"
    style={{
      width: 36,
      height: 36,
      border: 0,
      borderRadius: 9999,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      background: commentText.trim() ? "var(--current, #3B82F6)" : "#d8d8d8",
      color: "#fff",
      cursor: commentText.trim() ? "pointer" : "not-allowed",
      transition: "opacity .15s ease",
      opacity: commentText.trim() ? 1 : 0.7,
    }}
  >
    <i className="feather-send" />
  </button>
</form>


          <div className="right-0 bottom-0 pointer mt-2 text-grey-600 text-dark lh-26 font-xsssss" onClick={toggleComments}>
          {t("PostView119")}
          </div>
        </div>
      )}
    </div>
    </>
  );
};

export default Postview;
